import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';

import styles from './DropPanel.scss';

interface IDropPanelProps {
  hasParentFocus?: boolean;
  itemHeight?: number;
  maxItemsCount?: number;
  isActive: boolean;
  items: string[];
  fullwidth?: boolean;
  renderItem: (
    index: number,
    isSelected: boolean,
    isUpper: boolean,
    isLower: boolean,
    isSingle: boolean,
  ) => React.ReactNode;
  onEsc?: () => void;
  onSelect?: (index: number) => void;
  onIndexChange?: (index: number) => void;
  selectedIndex?: number;
}

const ITEM_HEIGHT_DEFAULT = 38;
const MAX_ITEM_COUNT_DEFAULT = 5;

const DropPanel = (props: IDropPanelProps) => {
  const scrollbarsRef = useRef<Scrollbars>(null);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(props.selectedIndex);
  const [panelWidth, setPanelWidth] = useState<number>(0);

  const {
    itemHeight = ITEM_HEIGHT_DEFAULT,
    maxItemsCount = MAX_ITEM_COUNT_DEFAULT,
    isActive,
    items,
    renderItem,
    onSelect,
    onIndexChange,
  } = props;

  const updateScrollPosition = (index: number) => {
    if (!scrollbarsRef.current || index < 0) return;

    const scrollbar = scrollbarsRef.current;
    const itemHeightValue = itemHeight ?? ITEM_HEIGHT_DEFAULT;
    const visibleItemsCount = maxItemsCount ?? MAX_ITEM_COUNT_DEFAULT;

    const currentScrollTop = scrollbar.getScrollTop();
    const viewportHeight = visibleItemsCount * itemHeightValue;

    const itemTop = index * itemHeightValue;
    const itemBottom = itemTop + itemHeightValue;

    if (itemTop < currentScrollTop) {
      scrollbar.scrollTop(itemTop);
    } else if (itemBottom > currentScrollTop + viewportHeight) {
      scrollbar.scrollTop(itemBottom - viewportHeight);
    }
  };

  const onKeyDown = (
    e: KeyboardEvent | React.KeyboardEvent<HTMLInputElement> | React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (!isActive) {
      return;
    }
    if (['ArrowUp', 'ArrowDown', 'Enter'].includes(e.key)) {
      e.preventDefault();
    }

    if (e.key === 'ArrowUp') {
      const newIndex = selectedIndex <= 0 ? items.length - 1 : selectedIndex - 1;
      setSelectedIndex(newIndex);
      onIndexChange?.(newIndex);
      updateScrollPosition(newIndex);
    }

    if (e.key === 'ArrowDown') {
      const newIndex = selectedIndex >= items.length - 1 ? 0 : selectedIndex + 1;
      setSelectedIndex(newIndex);
      onIndexChange?.(newIndex);
      updateScrollPosition(newIndex);
    }

    if (e.key === 'Enter') {
      onSelect?.(selectedIndex);
    }
  };

  useEffect(() => {
    if (isActive) {
      document.addEventListener('keydown', onKeyDown);
      if (items.length > 0 && selectedIndex === -1) {
        const initialIndex = props.hasParentFocus ? -1 : 0;
        setSelectedIndex(initialIndex);
      }
    }
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [selectedIndex, isActive]);

  useEffect(() => {
    if (props.selectedIndex !== undefined && props.selectedIndex !== selectedIndex) {
      setSelectedIndex(props.selectedIndex);
    }
  }, [props.selectedIndex]);

  const updateWidth = () => {
    if (props.fullwidth) {
      setPanelWidth(0);
      return;
    }

    let maxWidth = 0;
    itemsRef.current.forEach((item) => {
      if (item) {
        const contentWidth = item.scrollWidth - 32;
        maxWidth = Math.max(maxWidth, contentWidth);
      }
    });

    setPanelWidth(maxWidth + 32 + 4);
  };

  useEffect(() => {
    updateWidth();
  }, [props.items, props.fullwidth]);

  const onItemClick = (index: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIndex(index);
    onSelect?.(index);
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <Scrollbars
      ref={scrollbarsRef}
      autoHeight
      autoHeightMin={itemHeight ?? ITEM_HEIGHT_DEFAULT}
      autoHeightMax={
        (itemHeight ?? ITEM_HEIGHT_DEFAULT) * Math.min(maxItemsCount ?? MAX_ITEM_COUNT_DEFAULT, items.length)
      }
      renderTrackVertical={(props) => <div {...props} className={styles.scrollbarTrack} />}
      renderThumbVertical={(props) => <div {...props} className={styles.scrollbarThumb} />}
      style={{
        width: props.fullwidth ? '100%' : panelWidth > 0 ? `${panelWidth}px` : 'auto',
      }}
    >
      {items.map((item, index) => {
        const isSelected = index === selectedIndex;
        const isUpper = index === 0 && items.length !== 1;
        const isLower = index === items.length - 1 && items.length !== 1;
        const isSingle = items.length === 1;

        return (
          <div
            key={`item-${index}`}
            ref={(el) => (itemsRef.current[index] = el)}
            className={classnames(styles.item, {
              [styles.itemSelected]: isSelected,
              [styles.upperItem]: isUpper,
              [styles.lowerItem]: isLower,
              [styles.singleItem]: isSingle,
            })}
            onClick={onItemClick(index)}
            style={{ height: itemHeight }}
          >
            {renderItem(index, isSelected, isUpper, isLower, isSingle)}
          </div>
        );
      })}
    </Scrollbars>
  );
};

export default DropPanel;
